// Global Instructions Rule Applied!
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Space } from 'antd'
import { useSelector } from 'react-redux'
import { InputManager } from '../InputManager/index.ts'
import { FieldTypeEnum, type ContactFieldConfig, type FieldConfig } from '../InputManager/types.ts'

import Button from '../Button.jsx'
interface ContactFilterDropdownProps {
  selectedKeys: any[]
  setSelectedKeys: (keys: any[]) => void
  confirm: () => void
  clearFilters: () => void
  onFilterChange?: (filters: Array<{ key: string; value: any }>) => void
  filterKey: string
  filterLabel: string
}

interface FilterValues {
  contacts: any[] // Can be UUIDs or contact objects
}

/**
 * Contact Filter Dropdown Component for Fundraising Table
 * Provides multi-contact selection filtering for Lead, Introduced By, and Sourced By columns
 */
const ContactFilterDropdown: React.FC<ContactFilterDropdownProps> = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  onFilterChange,
  filterKey,
  filterLabel
}) => {
  const activeFilters = useSelector((state: any) => state.fundraising?.activeFilters || [])
  const filterOptions = useSelector((state: any) => state.fundraising?.filterOptions?.data || {})

  // Get contact options for this filter key
  const contactOptions = useMemo(() => {
    const options = filterOptions[filterKey] || []
    return options
  }, [filterOptions, filterKey])

  // Transform current filter value to contact objects for display
  // Priority: selectedKeys (from TableViewAnt) > activeFilters (from Redux)
  const transformedContacts = useMemo(() => {
    // Helper function to check if an array contains contact objects (with value and label) or just UUIDs
    const isContactObjects = (arr: any[]): boolean => {
      if (!arr || arr.length === 0) return false
      return arr.every(
        (item: any) => typeof item === 'object' && item !== null && item.value !== undefined && item.label !== undefined
      )
    }

    // Helper function to transform UUIDs to contact objects with labels
    const transformContactIdsToObjects = (contactIds: (string | any)[]) => {
      if (!contactIds || contactIds.length === 0) {
        return []
      }

      const transformed = contactIds.map((contactId: string | any) => {
        // Check if it's already an object with value/label
        if (typeof contactId === 'object' && contactId !== null && contactId.value !== undefined) {
          // Ensure it has a label property
          if (!contactId.label && contactId.name) {
            return { ...contactId, label: contactId.name }
          }
          return contactId
        }

        // contactId is a string (UUID), try to find the contact in filter options
        let option: any = null

        for (const opt of contactOptions) {
          if (opt.value === contactId) {
            option = opt
            break
          }
          if (opt.value === contactId?.toString()) {
            option = opt
            break
          }
          if (opt.id === contactId) {
            option = opt
            break
          }
          if (opt.id === contactId?.toString()) {
            option = opt
            break
          }
          if (String(opt.value) === String(contactId)) {
            option = opt
            break
          }
          if (String(opt.id) === String(contactId)) {
            option = opt
            break
          }
        }

        if (option) {
          // Return in the format ContactInput expects: { value, label }
          return {
            value: option.value || option.id || contactId,
            label: option.label || option.name || contactId,
            // Include other properties that might be useful
            ...(option.email && { email: option.email })
          }
        }

        // Fallback: return as-is (will show UUID until user searches or options load)
        return {
          value: contactId,
          label: contactId
        }
      })

      return transformed
    }

    // Extract filter value from selectedKeys (Ant Design wraps custom filters in an array)
    // selectedKeys format: [filterValue] where filterValue is the array of UUIDs
    let filterValueFromSelectedKeys: any[] | null = null
    if (selectedKeys && selectedKeys.length > 0) {
      const firstItem = selectedKeys[0]

      // If firstItem is an array, that's our filter value (array of UUIDs)
      if (Array.isArray(firstItem)) {
        filterValueFromSelectedKeys = firstItem
      } else if (typeof firstItem === 'string') {
        // If firstItem is not an array, selectedKeys might be the array of UUIDs directly
        // This shouldn't happen for custom filters, but handle it gracefully
        filterValueFromSelectedKeys = selectedKeys
      }
    }

    // First, try to get filter value from selectedKeys (passed from TableViewAnt)
    if (filterValueFromSelectedKeys && filterValueFromSelectedKeys.length > 0) {
      // Check if it's already contact objects (with labels stored)
      if (isContactObjects(filterValueFromSelectedKeys)) {
        return filterValueFromSelectedKeys
      }
      // Otherwise transform UUIDs to contact objects
      const contactIds = Array.isArray(filterValueFromSelectedKeys) ? filterValueFromSelectedKeys : []
      return transformContactIdsToObjects(contactIds)
    }

    // Fallback to activeFilters if selectedKeys is empty
    if (activeFilters && activeFilters.length > 0) {
      const contactFilter = activeFilters.find((filter: any) => filter.key === filterKey)
      const filterValue = contactFilter && contactFilter.value ? contactFilter.value : []

      if (Array.isArray(filterValue) && filterValue.length > 0) {
        // Check if it's already contact objects (with labels stored)
        if (isContactObjects(filterValue)) {
          return filterValue
        }
        // Otherwise transform UUIDs to contact objects
        return transformContactIdsToObjects(filterValue)
      }
    }

    return []
  }, [selectedKeys, activeFilters, filterKey, contactOptions])

  const [fieldValues, setFieldValues] = useState<FilterValues>({
    contacts: transformedContacts || []
  })

  useEffect(() => {
    // Update field values when transformed contacts change (e.g., when filter changes or options load)
    setFieldValues({
      contacts: transformedContacts || []
    })
  }, [transformedContacts, filterKey])

  const handleFieldChange = useCallback(
    (key: string, value: any) => {
      setFieldValues((prev) => {
        if (key === 'contacts') {
          // InputManager's ContactInput now returns contact objects with labels
          // But we still handle the case where UUIDs might be passed
          if (Array.isArray(value)) {
            // Check if values are already contact objects or just UUIDs
            const transformedContacts = value.map((item: any) => {
              // If it's already a contact object with value and label, use it
              if (typeof item === 'object' && item !== null && item.value !== undefined && item.label !== undefined) {
                return item
              }
              // Otherwise, it's a UUID string - find the contact in options to get the label
              const contactId = typeof item === 'string' ? item : item?.value || item?.id || item
              const option = contactOptions.find(
                (opt: any) =>
                  opt.value === contactId ||
                  opt.value === contactId?.toString() ||
                  opt.id === contactId ||
                  opt.id === contactId?.toString() ||
                  String(opt.value) === String(contactId) ||
                  String(opt.id) === String(contactId)
              )

              if (option) {
                return {
                  value: option.value || option.id || contactId,
                  label: option.label || option.name || contactId
                }
              }

              // Fallback: return UUID as both value and label
              return {
                value: contactId,
                label: contactId
              }
            })

            return { ...prev, [key]: transformedContacts }
          }
          return { ...prev, [key]: [] }
        }
        return { ...prev, [key]: value }
      })
    },
    [contactOptions]
  )

  const hasSelections = useMemo(() => {
    return fieldValues.contacts && fieldValues.contacts.length > 0
  }, [fieldValues.contacts])

  const handleApplyFilters = () => {
    if (hasSelections) {
      // Store the full contact objects (with labels) in filter state for display
      // This ensures we have the labels when reopening the dropdown
      const contactObjects = fieldValues.contacts.map((contact: any) => {
        // Ensure we have both value and label
        if (typeof contact === 'object' && contact !== null) {
          return {
            value: contact.value || contact.id || contact,
            label: contact.label || contact.name || contact.value || contact.id || contact
          }
        }
        // Fallback for string UUIDs (shouldn't happen, but handle gracefully)
        return {
          value: contact,
          label: contact
        }
      })

      const newFilterArray = [
        {
          key: filterKey,
          value: contactObjects // Store full objects with labels in state
        }
      ]

      // Set selectedKeys with contact objects wrapped in array (Ant Design format)
      setSelectedKeys([contactObjects])
      onFilterChange?.(newFilterArray)
    } else {
      setSelectedKeys([])
      onFilterChange?.([])
    }

    confirm()
  }

  const handleClearFilters = () => {
    // Clear field values
    setFieldValues({
      contacts: []
    })

    // Clear Ant Design's selectedKeys
    setSelectedKeys([])

    // Clear only this column's filter via onFilterChange
    // Empty array will be treated as "no meaningful content" and set to undefined
    onFilterChange?.([])

    // Close the dropdown
    confirm()
  }

  const fields: FieldConfig[] = useMemo(() => {
    const field: ContactFieldConfig = {
      key: 'contacts',
      type: FieldTypeEnum.CONTACT,
      value: fieldValues.contacts || [],
      mode: 'multiple',
      placeholder: `Select ${filterLabel}...`,
      editable: true,
      allowAddNew: false,
      size: 'middle',
      options: contactOptions // Provide options so ContactInput can resolve names
    }

    return [field]
    // eslint-disable-next-line
  }, [fieldValues, filterLabel, contactOptions, filterKey])

  return (
    <div style={{ padding: 8, width: 250 }}>
      <div style={{ marginBottom: 8 }}>
        {fields.map((field) => (
          <div key={field.key}>
            <InputManager
              inputMode='edit'
              fields={[field]}
              labelPosition='none'
              onChange={handleFieldChange}
              fieldWrapping={false}
            />
          </div>
        ))}
      </div>
      <Space>
        <Button variant='primary' size='small' onClick={handleApplyFilters} disabled={!hasSelections}>
          Filter
        </Button>
        <Button variant='cancel' size='small' onClick={handleClearFilters}>
          Clear
        </Button>
      </Space>
    </div>
  )
}

export default ContactFilterDropdown
